import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// UI Components
import { Box, Text } from '@/components/ui/primitives';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';

// Hooks and Utils
import { useToast } from '../hooks/useToast';
import { trpc, useTRPCClient } from '../lib/trpc';
import { isUnauthorizedError } from '../lib/authUtils';
import AppHeader from '@/components/AppHeader';
import { useAuth } from '@/hooks/useAuth';
import { clearPendingInviteCode, setPendingInviteCode, setRedirectAfterLogin } from '@/lib/storage';
import {
  getAuthHref,
  getLeagueIndexHref,
  normalizeBooleanParam,
  normalizeStringParam,
} from '@/lib/routes';

export default function JoinLeagueScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ inviteCode?: string | string[]; autoJoin?: string | string[] }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();
  const { isAuthenticated, isLoading } = useAuth();

  const [inviteCode, setInviteCode] = useState('');
  const autoJoinAttemptedRef = useRef(false);
  const routeInviteCode = normalizeStringParam(params.inviteCode)?.toUpperCase();
  const routeAutoJoin = routeInviteCode ? normalizeBooleanParam(params.autoJoin, true) : false;

  const redirectToAuthForJoin = useCallback(
    async (code?: string) => {
      const normalizedCode = code?.trim().toUpperCase();
      if (normalizedCode) {
        await setPendingInviteCode(normalizedCode);
      }
      await setRedirectAfterLogin({
        routeName: 'JoinLeague',
        params: normalizedCode ? { inviteCode: normalizedCode, autoJoin: true } : { autoJoin: true },
      });
      router.replace(
        getAuthHref({
          redirectTo: 'JoinLeague',
          inviteCode: normalizedCode,
          autoJoin: true,
        }),
      );
    },
    [router],
  );

  const joinLeagueMutation = useMutation({
    mutationFn: async (code: string) => {
      const normalizedCode = code.trim().toUpperCase();
      const response = await trpcClient.leagues.joinByInvite.mutate({
        inviteCode: normalizedCode,
      });
      const league = response?.league ?? response;
      if (!league?.id) {
        throw new Error('Join response is missing a league id.');
      }
      return { league, alreadyMember: response?.alreadyMember ?? false };
    },
    onSuccess: async ({ league }) => {
      toast.show({
        type: 'success',
        text1: 'Success',
        text2: 'Joined league successfully!',
      });
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      await queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      await clearPendingInviteCode();

      router.replace(getLeagueIndexHref(String(league.id)));
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        void redirectToAuthForJoin(inviteCode || routeInviteCode);
        return;
      }
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: error instanceof Error ? error.message : 'Failed to join league. Please check the code and try again.',
      });
    },
  });

  useEffect(() => {
    if (!routeInviteCode) {
      return;
    }
    setInviteCode(routeInviteCode.toUpperCase());
  }, [routeInviteCode]);

  useEffect(() => {
    autoJoinAttemptedRef.current = false;
  }, [routeInviteCode]);

  useEffect(() => {
    if (!routeInviteCode) {
      return;
    }
    if (!routeAutoJoin) {
      return;
    }
    if (autoJoinAttemptedRef.current) {
      return;
    }
    if (isLoading) {
      return;
    }
    if (!isAuthenticated) {
      void redirectToAuthForJoin(routeInviteCode);
      return;
    }
    autoJoinAttemptedRef.current = true;
    joinLeagueMutation.mutate(routeInviteCode);
  }, [isAuthenticated, isLoading, joinLeagueMutation, redirectToAuthForJoin, routeAutoJoin, routeInviteCode]);

  const handleSubmit = () => {
    if (!inviteCode.trim()) {
      toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Invite code is required',
      });
      return;
    }

    if (!isAuthenticated) {
      void redirectToAuthForJoin(inviteCode);
      return;
    }

    joinLeagueMutation.mutate(inviteCode.trim());
  };

  return (
    <Box flex={1} backgroundColor="background">
      <AppHeader title="Join League" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <Card>
          <CardContent style={styles.cardContent}>
            <Box marginBottom="l">
              <Text variant="h2" color="press" marginBottom="s">Join Existing League</Text>
              <Text variant="bodySmall" color="pressMuted" lineHeight={20}>
                Enter the invite code shared by your league commissioner to join the competition.
              </Text>
            </Box>

            <Box gap="l">
              <Box gap="s">
                <Label>Invite Code *</Label>
                <Input
                  placeholder="e.g., KICKOFF2024"
                  value={inviteCode}
                  onChangeText={setInviteCode}
                  autoCapitalize="characters"
                  autoCorrect={false}
                />
              </Box>

              <Button
                onPress={handleSubmit}
                disabled={joinLeagueMutation.isPending}
                marginTop="s"
                fluid
              >
                {joinLeagueMutation.isPending ? 'Joining...' : 'Join League'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </ScrollView>
    </Box>
  );
}

const styles = StyleSheet.create(theme => ({
  cardContent: {
    gap: theme.spacing.l,
    paddingTop: theme.spacing.l,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.m,
    paddingBottom: theme.spacing.xxl,
  },
}));
